import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_PRIORITIES } from "../../../utils/constants";
import { PriorityInterface } from "../../../models/TicketModule/Priority";
import priorityService from "../../../services/ticketService/priorityService";

interface DeletePriorityContext {
  previousPriorities: PriorityInterface[];
  deletedPriority: PriorityInterface | undefined;
}

const useDeletePriority = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the priority to delete
    DeletePriorityContext
  >({
    mutationFn: priorityService.Remove, // Remove should take a string ID

    onMutate: (priorityId: string) => {
      const previousPriorities =
        queryClient.getQueryData<PriorityInterface[]>([
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<PriorityInterface[]>(
        [
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousPriorities.filter((priority) => priority.id !== priorityId) // Use priorityId to filter
      );

      return {
        previousPriorities,
        deletedPriority: previousPriorities.find(
          (priority) => priority.id === priorityId
        ),
      };
    },

    onError: (error, priorityId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<PriorityInterface[]>(
        [
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousPriorities
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeletePriority;
